import { CpnPaymentReason } from '../../src/utils/cpn'

export interface CpnQuote {
  baseQuoteUuid: string
  blockchain: string
  cpnExchangeRate: string
  cpnExchangeRatePair: string
  estCpnFee: string
  cpnFeeCurrency: string
  createDate: string
  estDestinationAmount: string
  destinationCurrency: string
  estFeeRate: number
  paymentMethod: string
  quoteExpireDate: string
}

export interface PaytrieQuoteData {
  cadAmount: number
  cadFee: number
  cadUsdc: number
  usdcAmount: number
  cadUsdcTimestamp: string
}

export interface CpnQuoteResponse {
  cpn: CpnQuote[]
  cpnCached: boolean
  paytrie: PaytrieQuoteData
  limits: {
    min: number
    max: number
    currency: string
  }
  message: string
  status: 'success' | 'failure'
  recipientLimits?: {
    min: number
    max: number
    currency: string
  }
}

export interface CpnBeneficiaryUser {
  uuid: string
  firstName: string
  lastName: string
  street?: string
  city?: string
  stateProvince?: string
  postalCode?: string
  country?: string
  metadata?: Record<string, unknown>
  isBusiness: boolean
  isSelf: boolean
  paymentReason: CpnPaymentReason
}

export interface CpnPayment {
  id: string
  quoteId: string
  beneficiaryRefId: string
  customerRefId: string
  status: string
  blockchain: string
  sourceAmount: {
    amount: string
    currency: string
  }
  destinationAmount: {
    amount: string
    currency: string
  }
  createdAt: string
}

export interface CpnPayments {
  blockchain: string
  createdAt: string
  delayed: boolean
  expiresAt: string
  failureReason: string | null
  feeAmount: number
  feeCurrency: string
  fiatSettlementMax: number
  fiatSettlementMin: number
  fiatSettlementUnit: string
  fromAmount: number
  fromCurrency: string
  id: number
  intentId?: string
  paymentUuid: string
  quoteUuid: string
  status: string
  toAmount: number
  toCurrency: string
  useCase: string
  userUuid: string
  paymentMethod: string
  reasonForPayment: string
}

export interface CpnTransactions {
  amount: number
  currency: string
  expiresAt: string
  failureReason: string | null
  id: number
  paymentId: number
  status: string
  transactionUuid: string
  txHash: string
}

// Updated interfaces based on actual database schema
export interface CpnBeneficiaries {
  beneficiaryUuid: string
  uuid?: string // For backward compatibility
  city: string
  country: string
  firstName: string
  id: number
  isBusiness: boolean
  lastName: string
  metadata: Record<string, unknown> | null
  postalCode: string
  stateProvince: string
  street: string
  userUuid: string
}

export interface CpnLatestQuotes {
  blockchain: string
  createDate: string
  destinationAmount: string
  destinationCountry: string
  destinationCurrency: string
  exchangeRate: string
  exchangeRatePair: string
  feeAmount: string
  feeCurrency: string
  id: number
  paymentMethodType: string
  quoteExpireDate: string
  quoteUuid: string
  senderCountry: string
  sourceAmount: string
  sourceCurrency: string
}

export interface CpnRefunds {
  amount: number
  blockchain: string
  createdAt: string
  currency: string
  id: number
  paymentId: number
  refundUuid: string
  status: string
  txHash: string
}

export interface CpnRfis {
  expiresAt: string
  id: number
  level: string
  paymentId: number
  requiredFields: Record<string, unknown> | null
  rfiUuid: string
  status: string
}

export interface CpnPaymentsResponse {
  message: string
  status: 'success' | 'failure'
  payments: CpnPayments[]
}

export interface CpnStatusResponse {
  message: string
  status: 'success' | 'failure'
  payment: CpnPayments | null
  transactions: CpnTransactions[]
  rfis: CpnRfis[]
  cadAmount?: number
}

export const cpnSupportTicketTypes = [
  'PAYMENT_SETTLEMENT_DELAY',
  'PAYMENT_SENT_TO_WRONG_RECIPIENT',
  'PAYMENT_AMOUNT_INCORRECT',
  'REVERSE_FUNDS',
  'OTHER',
] as const
export type CpnSupportTicketType = (typeof cpnSupportTicketTypes)[number]

export interface CpnSupportTicket {
  id: string
  paymentId: string
  issueType: CpnSupportTicketType
  description: string
  caseRefId: string
  email: string
  ccEmails?: string[]
  createDate: string
}

export interface CpnSupportTicketRequest {
  paymentId: string
  issueType: CpnSupportTicketType
  description: string
  email: string
  ccEmails?: string[]
  idempotencyKey?: string
}

export interface CpnSupportTicketResponse {
  id: string
  paymentId: string
  issueType: CpnSupportTicketType
  description: string
  caseRefId: string
  email: string
  ccEmails?: string[]
  createDate: string
  message: string
  status: 'success'
}

export interface CpnApiResponse<T> {
  status: string
  message: string
  data?: T
}

export interface SwapQuoteRequest {
  leftSideValue: number // CAD amount
  blockchain?: string
  destinationCountry: string
  destinationCurrency: string
  paymentMethod?: string
  beneficiaryRefId: string
  magicWord?: string
  isFirstParty?: boolean
}

export interface SwapQuoteResponse {
  status: 'success'
  message: string
  id: string // Intent UUID
  requirements: object
  missing: {
    travelRule: string[]
    beneficiaryAccount: string[]
  }
  response: object
  paytrie: {
    id: number
    quoteId: number
    timestamp: string
    cadusd: number
    usdusdc: number
  }
  cpn: {
    quotes: Array<{
      id: number
      quoteUuid: string
      blockchain: string
      createDate: string
      destinationAmount: string
      destinationCountry: string
      destinationCurrency: string
      exchangeRate: string
      exchangeRatePair: string
      feeAmount: string
      feeCurrency: string
      paymentMethodType: string
      quoteExpireDate: string
      senderCountry: string
      sourceAmount: string
      sourceCurrency: string
    }>
    cachedQuotes: boolean
    quoteType: string
  }
}

export interface SwapPaymentRequest {
  ethCost?: number
  quoteId?: number
  gasId?: number
  leftSideLabel: string
  leftSideValue: number
  rightSideLabel?: string
  useReferral?: boolean
  vendorId?: string
  wallet?: string
  intentUuid: string
  useCase: 'C2C' | 'C2B' | 'B2C' | 'B2B'
  reasonForPayment: CpnPaymentReason
  magicWord?: string
  rfiMagicWord?: string
}

export interface SwapPaymentResponse {
  status: 'success'
  message: string
  tx: string
  rmt?: string
  cpnQuoteId: string
  cpnPaymentId: string
}

export type CpnConfiguration = {
  destinationCountry?: string
  destinationCurrency: string
  paymentMethodType: string
  amountLimit: {
    min: number
    max: number
    currency: string
  }
  lastUpdated: string
}

export type CpnConfigurationResponse = {
  configs: CpnConfiguration[]
  cachedConfigs: boolean
  message: string
  status: 'success' | 'failure'
}

// CPN Requirements Types
type CpnRequirementType = 'ADDRESS' | 'FILE' | 'TEXT'

export const cpnTravelRules = [
  'ADDRESS',
  'BENEFICIARY_ADDRESS',
  'BENEFICIARY_DATE_OF_BIRTH',
  'BENEFICIARY_FINANCIAL_INSTITUTION_NAME',
  'BENEFICIARY_EMAIL',
  'BENEFICIARY_NAME',
  'BENEFICIARY_NATIONAL_IDENTIFICATION_NUMBER',
  'BENEFICIARY_NATIONALITY',
  'BENEFICIARY_PHONE_NUMBER',
  'DATE_OF_BIRTH',
  'METHOD_OF_VERIFICATION',
  'NAME',
  'NATIONAL_IDENTIFICATION_NUMBER',
  'ORIGINATOR_ACCOUNT_NUMBER',
  'ORIGINATOR_ADDRESS',
  'ORIGINATOR_DATE_OF_BIRTH',
  'ORIGINATOR_FINANCIAL_INSTITUTION_NAME',
  'ORIGINATOR_FINANCIAL_INSTITUTION_ADDRESS',
  'ORIGINATOR_FINANCIAL_INSTITUTION_ID',
  'ORIGINATOR_NAME',
  'ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER',
  'ORIGINATOR_NATIONALITY',
  'ORIGINATOR_PERSON_IDENTIFIER',
  'RECIPIENT_PHONE_NUMBER',
  'SOURCE_OF_FUNDS',
] as const
export type CpnTravelRule = (typeof cpnTravelRules)[number]

export const cpnBeneficiaryRequirementTypes = [
  'ACCOUNT_NUMBER',
  'ACCOUNT_TYPE',
  'BANK_NAME',
  'BANK_CODE',
  'BANK_COUNTRY',
  'CLABE',
  'RECIPIENT_ADDRESS',
  'RECIPIENT_CITY',
  'RECIPIENT_ID_NUMBER',
  'RECIPIENT_LEGAL_NAME',
  'SWIFT_CODE',
] as const
export type CpnBeneficiaryRequirementType =
  (typeof cpnBeneficiaryRequirementTypes)[number]

export type CpnTravelRuleRequirement = {
  name: CpnTravelRule
  type: CpnRequirementType
  optional: boolean
}

export type CpnBeneficiaryRequirement = {
  name: CpnBeneficiaryRequirementType
  type: CpnRequirementType
  optional: boolean
}

export interface CpnRequirementsResponse {
  paymentRequirements: {
    travelRule: CpnTravelRuleRequirement[]
    beneficiaryAccount: CpnBeneficiaryRequirement[]
  }
  paymentResponse: Record<string, unknown>
  missingRequirements: {
    travelRule: CpnTravelRule[]
    beneficiaryAccount: CpnBeneficiaryRequirementType[]
  }
  message: string
  status: string
}

export interface CpnSwapStatusResponse {
  message: string
  status: 'success' | 'failure'
  intentId: string
  transactionId?: string
  paymentUuid?: string
  transactionStatus?: string
  cpnPayment?: Record<string, unknown>
  cpnTransactions?: Record<string, unknown>[]
  rfis?: Record<string, unknown>[]
  directStatusEndpoint?: string
  atbStatus?: string
  atbTransfers?: Record<string, unknown>[]
  statusEndpoint?: string
}
